import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AlertBox } from '@/common/AlertBox';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { AddPartnerForm } from '@/follow-up-partners/AddPartnerForm';
import { PageContainer } from '~/styles/boardStyles';
import {
  FormContainer,
  GroupHeaderWrapper,
  Title,
} from '~/styles/layoutElements';

export const Route = createFileRoute('/_app/follow-up-partners/new')({
  component: AddFollowUpPartnerScreen,
});

/** How long the confirmation shows on this page before the redirect. */
const CONFIRMATION_HOLD_MS = 1800;

/**
 * The Add New Follow-up Partner page.
 *
 * No `creator_id` or `group_id` inputs: legacy took both from the form, but
 * both are now the signed-in coordinator, so the request carries neither.
 *
 * Legacy confirms with a flash message shown on the page it redirects to.
 * There's no app-level message channel to carry it across the redirect, so the
 * message shows here over the spent form and the redirect follows after
 * `CONFIRMATION_HOLD_MS`. The new row is at the top of the directory it lands
 * on.
 *
 * The gate lives in `follow-up-partners.tsx`.
 */
function AddFollowUpPartnerScreen(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [created, setCreated] = useState(false);

  const backToDirectory = (): void => {
    void navigate({ to: '/follow-up-partners/' });
  };

  useEffect(() => {
    if (!created) return;
    const timer = window.setTimeout(() => {
      void navigate({ to: '/follow-up-partners/' });
    }, CONFIRMATION_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [created, navigate]);

  return (
    <PageContainer>
      <GroupHeaderWrapper>
        {/* A bare `Title`, as legacy has no help placement here. */}
        <Title>{t('actions.add-new-follow-up-partner')}</Title>
      </GroupHeaderWrapper>

      {/* Required content, shown above the form as in legacy. Legacy's
         sentence has no link; here the whole sentence links to the
         network, because the catalogue string can't be split around those
         words in every language. */}
      <Preamble>
        <Link
          to="/follow-up-network/"
          data-testid="add-partner-network-preamble"
        >
          {t('wbs.before-you-manually-enter')}
        </Link>
      </Preamble>

      {/* Rendered above the form rather than replacing it: the form stays
         disabled after a 201, and swapping it out would collapse the page
         height during the hold. */}
      {created && (
        <AlertBox variant="success">
          <span data-testid="add-partner-confirmation">
            {t('actions.follow-up-partner-added-successfully')}
          </span>
        </AlertBox>
      )}

      <FormContainer>
        {/* Back to the partner directory, where legacy's back button goes. */}
        <LargeArrowBackButton onClick={backToDirectory} />
        <AddPartnerForm
          onCreated={() => {
            setCreated(true);
          }}
        />
      </FormContainer>
    </PageContainer>
  );
}

const Preamble = styled.p`
  font-family: var(--raleway);
  font-size: 14px;
  line-height: 20px;
  margin: 0 0 24px 0;

  a {
    color: var(--wbs-red);
    text-decoration: none;

    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
`;
